import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { TOOLS } from "../lib/atomika-tools";
import { useOverview } from "../lib/atomika-overview";
import { canAccessArea, isAdmin, useAuth } from "../lib/auth";

const NAME_BY_PATH: Record<string, string> = {
  "/": "Atomika stjórnborð",
  "/farsimi": "Atomika stjórnborð",
  "/verkfaeri/atomika-reiknivel": "Atomika reiknivél",
  "/verkfaeri/atomika-maelabord": "Atomika mælaborð",
  "/verkfaeri/spalikan-orkunotkun": "Spálíkan orkunotkunar",
  "/verkfaeri/atomika-heimasida": "Atomika heimasíða",
  "/verkfaeri/markadurinn": "Atomika CRM",
  "/admin/notendur": "Notendaumsjón",
};

const ROLE_LABEL: Record<string, string> = {
  superadmin: "Yfirstjórnandi",
  admin: "Stjórnandi",
  user: "Notandi",
};

function initialsOf(label: string): string {
  const parts = label
    .trim()
    .split(/[\s@.]+/)
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function TopBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isHome = pathname === "/";
  const currentName = NAME_BY_PATH[pathname] ?? "Atomika stjórnborð";

  const overview = useOverview();
  const notifs = overview?.notifications ?? [];

  const visibleTools = TOOLS.filter((t) => canAccessArea(user, t.id));
  const label = user?.displayName || user?.email || "";
  const initials = initialsOf(label);

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
        setNotifOpen(false);
        setUserOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div
      ref={barRef}
      className="sticky top-0 z-50 flex h-16 items-center gap-[18px] border-b border-line bg-white px-6"
    >
      <Link to="/" className="flex cursor-pointer items-center">
        <img
          src="/logo/atomika-horizontal.svg"
          alt="Atomika"
          className="block h-7 w-auto"
        />
      </Link>

      <div className="h-[26px] w-px bg-line" />
      <span className="text-sm font-semibold text-muted">{currentName}</span>

      <div className="flex-1" />

      {isHome ? (
        <span className="text-[13px] font-semibold text-muted-3">
          Sameiginlegt vinnusvæði
        </span>
      ) : (
        <button
          onClick={() => navigate({ to: "/" })}
          className="flex h-[38px] cursor-pointer items-center gap-[7px] rounded-[10px] border border-line bg-white px-3.5 text-[13px] font-semibold text-ink"
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1E7BFF"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Stjórnborð
        </button>
      )}

      {/* App switcher */}
      {visibleTools.length > 0 && (
        <div className="relative">
          <button
            onClick={() => {
              setSwitcherOpen((v) => !v);
              setNotifOpen(false);
              setUserOpen(false);
            }}
            title="Skipta um lausn"
            className="flex h-[38px] w-[38px] cursor-pointer items-center justify-center rounded-[10px] border border-line bg-white"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#1E7BFF">
              <rect x="3" y="3" width="7" height="7" rx="1.6" />
              <rect x="14" y="3" width="7" height="7" rx="1.6" />
              <rect x="3" y="14" width="7" height="7" rx="1.6" />
              <rect x="14" y="14" width="7" height="7" rx="1.6" />
            </svg>
          </button>
          {switcherOpen && (
            <div className="absolute right-0 top-12 w-[340px] animate-[fadeUp_.16s_ease] rounded-2xl border border-line bg-white p-2.5 shadow-[0_24px_60px_rgba(11,36,71,.16)]">
              <div className="px-2.5 pb-2.5 pt-2 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-3">
                Atomika lausnir
              </div>
              {visibleTools.map((t) => (
                <Link
                  key={t.id}
                  to={t.route}
                  onClick={() => setSwitcherOpen(false)}
                  className="flex cursor-pointer items-center gap-3 rounded-xl p-2.5 transition-colors hover:bg-canvas"
                >
                  <div
                    className="flex h-10 w-10 flex-none items-center justify-center rounded-[11px] font-display text-base font-extrabold"
                    style={{ background: t.bg, color: t.color }}
                  >
                    {t.glyph}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-bold text-ink">
                      {t.name}
                    </div>
                    <div className="text-[11.5px] text-muted-2">{t.tag}</div>
                  </div>
                  <span
                    className="h-2 w-2 flex-none rounded-full"
                    style={{ background: t.dot }}
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => {
            setNotifOpen((v) => !v);
            setSwitcherOpen(false);
            setUserOpen(false);
          }}
          title="Tilkynningar"
          className="relative flex h-[38px] w-[38px] cursor-pointer items-center justify-center rounded-[10px] border border-line bg-white"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5B6B85"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          <span className="absolute right-[7px] top-1.5 h-2 w-2 rounded-full border-[1.5px] border-white bg-danger" />
        </button>
        {notifOpen && (
          <div className="absolute right-0 top-12 w-[360px] animate-[fadeUp_.16s_ease] rounded-2xl border border-line bg-white p-2 shadow-[0_24px_60px_rgba(11,36,71,.16)]">
            <div className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-3">
              Tilkynningar úr öllum lausnum
            </div>
            {notifs.map((n, i) => (
              <div
                key={i}
                className="flex gap-[11px] rounded-[11px] px-3 py-[11px] hover:bg-[#F7FAFF]"
              >
                <span
                  className="mt-[5px] h-[9px] w-[9px] flex-none rounded-full"
                  style={{ background: n.dot }}
                />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold leading-[1.35] text-ink">
                    {n.text}
                  </div>
                  <div className="mt-0.5 text-[11.5px] text-muted-3">
                    {n.meta}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => {
            setUserOpen((v) => !v);
            setSwitcherOpen(false);
            setNotifOpen(false);
          }}
          title={label}
          aria-label="Notandavalmynd"
          aria-haspopup="menu"
          aria-expanded={userOpen}
          className="flex h-[38px] cursor-pointer items-center gap-1.5 rounded-full border border-line bg-white py-1 pl-1 pr-2"
        >
          <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-gradient-to-br from-brand to-brand-dark text-[13px] font-bold text-white">
            {initials}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#5B6B85"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${userOpen ? "rotate-180" : ""}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {userOpen && (
          <div className="absolute right-0 top-12 w-[240px] animate-[fadeUp_.16s_ease] rounded-2xl border border-line bg-white p-2 shadow-[0_24px_60px_rgba(11,36,71,.16)]">
            <div className="border-b border-line px-3 pb-2.5 pt-1.5">
              <div className="truncate text-[13.5px] font-bold text-ink">
                {user?.displayName || user?.email}
              </div>
              <div className="mt-0.5 text-[11.5px] text-muted-2">
                {ROLE_LABEL[user?.role ?? "user"]}
              </div>
            </div>
            {isAdmin(user) && (
              <Link
                to="/admin/notendur"
                onClick={() => setUserOpen(false)}
                className="mt-1 flex cursor-pointer items-center gap-2 rounded-[10px] px-3 py-2 text-[13px] font-semibold text-ink hover:bg-canvas"
              >
                Notendaumsjón
              </Link>
            )}
            <button
              onClick={() => {
                setUserOpen(false);
                void logout();
              }}
              className="mt-1 flex w-full cursor-pointer items-center gap-2 rounded-[10px] px-3 py-2 text-left text-[13px] font-semibold text-danger hover:bg-danger/10"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <path d="M16 17l5-5-5-5" />
                <path d="M21 12H9" />
              </svg>
              Skrá út
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
